import 'querystring';
import 'react';
import calculatePayment, { monthlyPMI } from '@zillow/mortgage-calculations';
import '@zillow/mortgage-webview-query-params';
import { LOAN_PROGRAMS, COMMON_AMOUNT_CONFIG, COMMON_PERCENT_CONFIG, LOAN_TERM_IN_MONTHS } from '../../utils/zhl-calculator.js';
import { useLastValidValues } from '../../utils/form/useLastValidValues.js';
import PropTypes from 'prop-types';

var ARM_PROGRAMS = [LOAN_PROGRAMS.arm3, LOAN_PROGRAMS.arm5, LOAN_PROGRAMS.arm7];
var DEFAULT_VALUES_SHAPE = {
  homePrice: PropTypes.number,
  zipCode: PropTypes.number,
  loanProgram: PropTypes.number,
  downPayment: PropTypes.number,
  downPaymentPercent: PropTypes.number,
  interestRate: PropTypes.number,
  taxRate: PropTypes.number,
  taxRatePercent: PropTypes.number,
  homeInsurance: PropTypes.number,
  hoa: PropTypes.number,
  includeMIP: PropTypes.bool,
  includePMI: PropTypes.bool,
  includeTaxesInsurance: PropTypes.bool
};
var FieldConfig = {
  homePrice: {
    name: 'homePrice',
    config: Object.assign({}, COMMON_AMOUNT_CONFIG)
  },
  downPayment: {
    name: 'downPayment',
    config: Object.assign({}, COMMON_AMOUNT_CONFIG)
  },
  downPaymentPercent: {
    name: 'downPaymentPercent',
    config: Object.assign({}, COMMON_PERCENT_CONFIG)
  },
  zipCode: {
    name: 'zipCode'
  },
  loanProgram: {
    name: 'loanProgram',
    options: [{
      value: LOAN_PROGRAMS.fixed30Year,
      label: '30-year fixed'
    }, {
      value: LOAN_PROGRAMS.fixed15Year,
      label: '15-year fixed'
    }, {
      value: LOAN_PROGRAMS.arm5,
      label: '5-year ARM'
    }],
    config: {
      required: 'Required',
      validate: {
        invalidProgram: function invalidProgram(value) {
          var error = null;
          if (Object.values(LOAN_PROGRAMS).includes(value) === false) {
            error = 'Invalid loan program';
          }
          return error;
        }
      }
    }
  },
  fhaLoanProgram: {
    name: 'loanProgram',
    options: [{
      value: LOAN_PROGRAMS.fixed30Year,
      label: '30-year fixed'
    }, {
      value: LOAN_PROGRAMS.fixed15Year,
      label: '15-year fixed'
    }],
    config: {
      required: 'Required',
      validate: {
        invalidProgram: function invalidProgram(value) {
          var error = null;
          if (Object.values(LOAN_PROGRAMS).includes(value) === false) {
            error = 'Invalid loan program';
          }
          return error;
        }
      }
    }
  },
  interestRate: {
    name: 'interestRate',
    config: Object.assign({}, COMMON_PERCENT_CONFIG)
  },
  includeTaxesInsurance: {
    name: 'includeTaxesInsurance'
  },
  includeMIP: {
    name: 'includeMIP'
  },
  taxRate: {
    name: 'taxRate',
    config: Object.assign({}, COMMON_AMOUNT_CONFIG)
  },
  taxRatePercent: {
    name: 'taxRatePercent',
    config: Object.assign({}, COMMON_PERCENT_CONFIG)
  },
  homeInsurance: {
    name: 'homeInsurance',
    config: Object.assign({}, COMMON_AMOUNT_CONFIG)
  },
  hoa: {
    name: 'hoa',
    config: Object.assign({}, COMMON_AMOUNT_CONFIG)
  },
  includePMI: {
    name: 'includePMI'
  }
};
function useLastValidPayemntValues(form) {
  return useLastValidValues(form, getPayementValues);
}
function getPayementValues(lastValidFields) {
  var calculations = calculatePayment({
    term: LOAN_TERM_IN_MONTHS[lastValidFields.loanProgram],
    homePrice: lastValidFields.homePrice,
    downPayment: lastValidFields.downPayment,
    rate: lastValidFields.interestRate,
    includeHOA: true,
    monthlyHOA: lastValidFields.hoa,
    includeHomeownersInsurance: lastValidFields.includeTaxesInsurance,
    annualHomeownersInsurance: lastValidFields.homeInsurance,
    includePropertyTax: lastValidFields.includeTaxesInsurance,
    propertyTaxAmount: lastValidFields.taxRate,
    propertyTaxRate: lastValidFields.taxRatePercent,
    includePMI: false,
    includeTaxesInsurance: true,
    isARM: ARM_PROGRAMS.includes(lastValidFields.loanProgram),
    dti: 0
  });
  var values = {
    fields: lastValidFields,
    calculations: calculations
  };
  return values;
}
var safe = function safe(fn, fallback) {
  if (fallback === void 0) {
    fallback = null;
  }
  return function (x) {
    return x ? fn(x) : fallback;
  };
};
function isSameFloat(float1, float2) {
  return Math.abs(Number(float1.toFixed(2)) - Number(float2.toFixed(2))) < Number.EPSILON;
}
function calculateMIPRate(ltv, loanAmount, loanprogram) {
  var monthlyMIP;
  if (loanprogram == 'Fixed30Year') {
    if (ltv > 90 && ltv <= 95) {
      monthlyMIP = loanAmount <= 726200 ? 0.005 : 0.007;
    }
    if (ltv > 95) {
      monthlyMIP = loanAmount <= 726200 ? 0.0055 : 0.0075;
    }
  } else {
    if (loanAmount >= 726200) {
      if (ltv <= 78) {
        monthlyMIP = 0.0015;
      }
      if (ltv >= 78 && ltv <= 90) {
        monthlyMIP = 0.004;
      }
      if (ltv > 90) {
        monthlyMIP = 0.0065;
      }
    }
  }
  return monthlyMIP / 12;
}
function calculateMonthlyMIP(loanAmountValue, propertyValue, loanProgram) {
  var ltv = loanAmountValue / propertyValue * 100;
  var mipRate = calculateMIPRate(ltv, loanAmountValue, loanProgram);
  var monthlyMIP = mipRate * loanAmountValue / 12;
  return monthlyMIP;
}
function calculateMonthlyPMI(homePrice, downPaymentValue, loanProgram) {
  var loanAmount = homePrice - downPaymentValue;
  var isARM = ARM_PROGRAMS.includes(loanProgram);
  var monthlyMIP = monthlyPMI(loanAmount, homePrice, isARM);
  return monthlyMIP;
}
var vaCalculatorDisclaimerText = 'The VA mortgage calculator is intended to be used for educational purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. The estimates are based on information you provide, and may not include other fees and costs that a lender may assess in addition to monthly principal and interest, such VA funding fee, other taxes and insurance,and the actual payment obligation may be greater. Zillow Group Marketplace, Inc. does not make loans and this is not a commitment to lend.';
var mortgageCalculatorDisclaimerText = 'The mortgage calculator is intended to be used for educational purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. The estimates are based on information you provide, and may not include other fees and costs that a lender may assess in addition to monthly principal and interest, such as taxes and insurance and the actual payment obligation may be greater. Zillow Group Marketplace, Inc. does not make loans and this is not a commitment to lend.';
var fhaCalculatorDisclaimerText = 'The FHA mortgage calculator is intended to be used for educational purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. The estimates are based on information you provide, and may not include other fees and costs that a lender may assess in addition to monthly principal and interest, such as taxes and insurance and the actual payment obligation may be greater. Zillow Group Marketplace, Inc. does not make loans and this is not a commitment to lend.';

export { DEFAULT_VALUES_SHAPE, FieldConfig, calculateMonthlyMIP, calculateMonthlyPMI, fhaCalculatorDisclaimerText, getPayementValues, isSameFloat, mortgageCalculatorDisclaimerText, safe, useLastValidPayemntValues, vaCalculatorDisclaimerText };
